import { useAuth } from '../contexts/AuthContext';
import { Card, Badge } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';
import { HiUser, HiShieldCheck, HiUsers, HiOfficeBuilding, HiDeviceMobile } from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';

export default function Dashboard() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  // Si es superadmin, usar AdminLayout
  if (userProfile?.role === 'superadmin') {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">Bienvenido a Domus Secura</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow bg-white dark:bg-gray-800"
              onClick={() => navigate('/admin/users')}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Usuarios</h3>
                <HiUsers className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">Gestionar</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Administrar usuarios del sistema</p>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Tenants</h3>
                <HiOfficeBuilding className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">Gestionar</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Administrar tenants</p>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Dispositivos</h3>
                <HiDeviceMobile className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">Gestionar</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Administrar dispositivos</p>
            </Card>
          </div>

          <Card className="bg-white dark:bg-gray-800">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <HiUser className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Mi Perfil</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Nombre</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{userProfile?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{user?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Teléfono</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{userProfile?.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Rol</p>
                  <Badge color="blue" icon={HiShieldCheck} className="w-fit">
                    {userProfile?.role || 'N/A'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Estado</p>
                  <Badge 
                    color={userProfile?.status === 'active' ? 'success' : userProfile?.status === 'pending_approval' ? 'warning' : 'failure'}
                    className="w-fit"
                  >
                    {userProfile?.status === 'active' ? 'Activo' :
                     userProfile?.status === 'pending_approval' ? 'Pendiente' : 'Inactivo'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  // Usuario normal
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-300">Bienvenido a Domus Secura</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card className="bg-white dark:bg-gray-800">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <HiUser className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Mi Perfil</h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">Nombre:</strong> {userProfile?.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">Email:</strong> {user?.email || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">Teléfono:</strong> {userProfile?.phone || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">Rol:</strong> {userProfile?.role || 'N/A'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-white dark:bg-gray-800">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Estado de la Cuenta</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                <strong className="text-gray-900 dark:text-white">Estado:</strong>{' '}
                <Badge 
                  color={userProfile?.status === 'active' ? 'success' : userProfile?.status === 'pending_approval' ? 'warning' : 'failure'}
                  className="ml-2"
                >
                  {userProfile?.status === 'active' ? 'Activo' :
                   userProfile?.status === 'pending_approval' ? 'Pendiente' : 'Inactivo'}
                </Badge>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
